import React, { FunctionComponent } from 'react';

import styled, { css } from 'styled-components';
import { Translate } from 'react-localize-redux';

import { ReleaseStatus } from '@common/types/release/relase-status';

import Flex from '@components/layout/flex';
import QuestionMarkHelper from '@components/basics/question-mark-helper';
import Typography from '@components/basics/typography';

import { getStatusLabel } from '@webapp/store/release/selectors';

interface StatusLabelProps {
  status?: ReleaseStatus;
  releaseDate?: Date | string;
  approved?: boolean;
  handleStatusClick?: () => void;
}

const StatusLabel: FunctionComponent<StatusLabelProps> = ({
  status,
  releaseDate,
  approved,
  handleStatusClick,
}) => {
  const text = getStatusLabel(status, releaseDate, approved);
  return (
    <Label
      alignItems="center"
      justifyContent="center"
      onClick={handleStatusClick}
      style={{ cursor: handleStatusClick ? 'pointer' : 'normal' }}
    >
      <LabelText
        status={status}
        approved={approved}
        capitalize
        weight="700"
        type="small"
      >
        {text}
      </LabelText>
      {status === 'pending-processing' && <div>&nbsp;</div>}
      {status === 'pending-processing' && (
        <QuestionMarkHelper id="status" background="#E6E7EB" color="#32343C">
          <Translate id="CATALOG.PENDING_PROCESSING.STATUS.HELPER" />
        </QuestionMarkHelper>
      )}
    </Label>
  );
};

export default StatusLabel;

const Label = styled(Flex)`
  padding: 8px;
  border-radius: 100px;
  background-color: ${({ theme }) => theme.colors.differentBackground};
  min-width: 100px;
  max-width: 120px;

  @media ${({ theme }) => theme.device.tablet} {
    min-width: none;
  }
`;

const LabelText = styled(Typography)<{
  status?: ReleaseStatus;
  approved?: boolean;
}>`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  min-width: 100px;

  @media ${({ theme }) => theme.device.tablet} {
    min-width: auto;
  }

  ${({ status, approved }) =>
    status === 'draft'
      ? css`
          color: ${({ theme }) => theme.colors.tertiary};
        `
      : status === 'pending' ||
        status === 'pending-processing' ||
        status === 'changes-needed'
      ? css`
          color: ${({ theme }) => theme.colors.upload};
        `
      : status === 'taken-down' ||
        status === 'rejected' ||
        status === 'pending-takedown'
      ? css`
          color: ${({ theme }) => theme.colors.error};
        `
      : status === 'released'
      ? css`
          color: ${({ theme }) => theme.colors.primary};
        `
      : approved || status === 'approved' || status === 'published'
      ? css`
          color: ${({ theme }) => theme.colors.successButton};
        `
      : css`
          color: ${({ theme }) => theme.colors.primary};
        `}
`;
